import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  DEFAULT_AUTHOR,
  DEFAULT_OG_IMAGE,
  DEFAULT_ROBOTS,
  SITE_NAME,
  buildStructuredData,
  normalizeCanonicalPath,
  toAbsoluteUrl,
} from '../seo/constants';
import { useSeoRegistry } from '../seo/registry';

const MANAGED_ATTR = 'data-seo-managed';
const STATIC_ATTR = 'data-seo-static';

/**
 * SEO Meta Tags Component
 *
 * During render it publishes the page metadata to the SeoRegistry (read by the
 * SSR entry to emit real <head> tags and by SeoPageLayout for visible FAQs).
 * In the browser it also keeps <head> in sync on client-side navigation.
 */
export default function SeoMetaTags({
  title,
  description,
  keywords,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  robots = DEFAULT_ROBOTS,
  canonicalPath,
  twitterCard = "summary",
  structuredData,
  author = DEFAULT_AUTHOR,
  publisher = SITE_NAME,
}) {
  const location = useLocation();
  const registry = useSeoRegistry();
  const routePath = normalizeCanonicalPath(location.pathname);
  const effectivePath = canonicalPath || location.pathname;
  const canonicalUrl = toAbsoluteUrl(effectivePath);

  if (registry) {
    registry.set(routePath, {
      routePath,
      title,
      description,
      keywords,
      ogImage,
      ogType,
      robots,
      canonicalPath: normalizeCanonicalPath(effectivePath),
      canonicalUrl,
      twitterCard,
      structuredData,
      author,
      publisher,
    });
  }

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const head = document.head;
    head.querySelectorAll(`[${MANAGED_ATTR}="true"]`).forEach((el) => el.remove());
    head.querySelectorAll(`[${STATIC_ATTR}="true"]`).forEach((el) => el.remove());

    if (title) document.title = title;

    const appendManagedTag = (node) => {
      node.setAttribute(MANAGED_ATTR, 'true');
      head.appendChild(node);
      return node;
    };

    const createMeta = (attrs, content) => {
      if (!content) return;
      const selector = Object.entries(attrs)
        .map(([k, v]) => `meta[${k}="${v}"]`)
        .join('');
      let tag = head.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => tag.setAttribute(k, v));
        appendManagedTag(tag);
      } else {
        tag.setAttribute(MANAGED_ATTR, 'true');
      }
      tag.setAttribute('content', content);
    };

    if (description) createMeta({ name: 'description' }, description);
    if (keywords) createMeta({ name: 'keywords' }, keywords);
    createMeta({ name: 'author' }, author);
    createMeta({ name: 'creator' }, author);
    createMeta({ name: 'publisher' }, publisher);
    createMeta({ name: 'robots' }, robots);

    let canonical = head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      appendManagedTag(canonical);
    } else {
      canonical.setAttribute(MANAGED_ATTR, 'true');
    }
    canonical.setAttribute('href', canonicalUrl);

    createMeta({ property: 'og:title' }, title);
    createMeta({ property: 'og:description' }, description);
    createMeta({ property: 'og:url' }, canonicalUrl);
    createMeta({ property: 'og:image' }, ogImage);
    createMeta({ property: 'og:image:alt' }, 'DJ Request logo');
    createMeta({ property: 'og:locale' }, 'en_US');
    createMeta({ property: 'og:type' }, ogType);
    createMeta({ property: 'og:site_name' }, SITE_NAME);

    createMeta({ name: 'twitter:card' }, twitterCard);
    createMeta({ name: 'twitter:title' }, title);
    createMeta({ name: 'twitter:description' }, description);
    createMeta({ name: 'twitter:image' }, ogImage);
    createMeta({ name: 'twitter:image:alt' }, 'DJ Request logo');

    buildStructuredData({
      title,
      description,
      canonicalPath: effectivePath,
      routePath,
      structuredData,
    }).forEach((item) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(item);
      appendManagedTag(script);
    });
  }, [
    title,
    description,
    keywords,
    ogImage,
    ogType,
    robots,
    canonicalUrl,
    twitterCard,
    structuredData,
    author,
    publisher,
    effectivePath,
    routePath,
  ]);

  return null;
}
